import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { Input } from '../../components/ui/input';
import {
  Download,
  TrendingUp,
  DollarSign,
  Building2,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { getAgentReport, getBankReport, getRevenueReport } from '../../lib/api';

const formatCurrency = (amount) => {
  const value = Number(amount || 0);
  if (value >= 10000000) return `Rs ${(value / 10000000).toFixed(2)} Cr`;
  if (value >= 100000) return `Rs ${(value / 100000).toFixed(2)} L`;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
};

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;

const renderTableMessage = (colSpan, message) => (
  <TableRow>
    <TableCell colSpan={colSpan} className="py-10 text-center text-muted-foreground">
      {message}
    </TableCell>
  </TableRow>
);

const buildPaginationItems = (page, totalPages) => {
  const pages = [];
  for (let current = 1; current <= totalPages; current += 1) {
    if (current === 1 || current === totalPages || Math.abs(current - page) <= 1) {
      pages.push(current);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return pages;
};

function TablePagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  const items = buildPaginationItems(page, totalPages);
  return (
    <Pagination className="justify-end">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page > 1) onPageChange(page - 1);
            }}
            className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
        {items.map((item, index) => (
          <PaginationItem key={`${item}-${index}`}>
            {item === '...' ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                href="#"
                isActive={item === page}
                onClick={(event) => {
                  event.preventDefault();
                  onPageChange(item);
                }}
              >
                {item}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}
        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page < totalPages) onPageChange(page + 1);
            }}
            className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

const trendMeta = (value) => {
  const numeric = Number(value || 0);
  const positive = numeric >= 0;
  return {
    Icon: positive ? ArrowUpRight : ArrowDownRight,
    className: positive ? 'text-emerald-500' : 'text-red-500',
    label: `${positive ? '+' : ''}${numeric.toFixed(1)}%`,
  };
};

const rangeToMonths = {
  '1month': 1,
  '3months': 3,
  '6months': 6,
  '1year': 12,
};

export const RevenueReport = () => {
  const { token } = useAuth();
  const [range, setRange] = useState('6months');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    async function load() {
      if (!token) return;
      setLoading(true);
      setError('');
      try {
        const resp = await getRevenueReport(token, { months: rangeToMonths[range] || 6 });
        if (active) setData(resp?.data || null);
      } catch (err) {
        if (!active) return;
        setData(null);
        setError(err?.message || 'Failed to load revenue report');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [range, token]);

  const summary = data?.summary || {};
  const trends = data?.trends || {};
  const chartData = Array.isArray(data?.chart) ? data.chart : [];
  const revenueTrend = trendMeta(trends.revenue);
  const commissionTrend = trendMeta(trends.commissionPaid);
  const profitTrend = trendMeta(trends.netProfit);

  return (
    <div className="space-y-6" data-testid="revenue-report">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Revenue Report</h1>
          <p className="text-muted-foreground mt-1">Track revenue and commission trends</p>
        </div>
        <div className="flex gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1month">Last Month</SelectItem>
              <SelectItem value="3months">3 Months</SelectItem>
              <SelectItem value="6months">6 Months</SelectItem>
              <SelectItem value="1year">1 Year</SelectItem>
            </SelectContent>
          </Select>
          {/* <Button variant="outline" disabled>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button> */}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="border border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.totalRevenue)}</p>
                <div className="flex items-center gap-1 mt-1">
                  <revenueTrend.Icon className={`h-4 w-4 ${revenueTrend.className}`} />
                  <span className={`text-xs ${revenueTrend.className}`}>{revenueTrend.label}</span>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-emerald-100 text-emerald-600">
                <TrendingUp className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Commission Paid</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.commissionPaid)}</p>
                <div className="flex items-center gap-1 mt-1">
                  <commissionTrend.Icon className={`h-4 w-4 ${commissionTrend.className}`} />
                  <span className={`text-xs ${commissionTrend.className}`}>{commissionTrend.label}</span>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-purple-100 text-purple-600">
                <DollarSign className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Profit</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.netProfit)}</p>
                <div className="flex items-center gap-1 mt-1">
                  <profitTrend.Icon className={`h-4 w-4 ${profitTrend.className}`} />
                  <span className={`text-xs ${profitTrend.className}`}>{profitTrend.label}</span>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-blue-100 text-blue-600">
                <TrendingUp className="h-5 w-5" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Revenue vs Commission</CardTitle>
          <CardDescription>Monthly comparison for the selected range</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <div className="h-[350px] flex items-center justify-center text-muted-foreground">Loading report...</div> : null}
          {!loading && error ? <div className="h-[350px] flex items-center justify-center text-muted-foreground">{error}</div> : null}
          {!loading && !error && chartData.length === 0 ? (
            <div className="h-[350px] flex items-center justify-center text-muted-foreground">No report data found.</div>
          ) : null}
          {!loading && !error && chartData.length > 0 ? (
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorCommission" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(value) => formatCurrency(value)} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [formatCurrency(value), '']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#10B981" fillOpacity={1} fill="url(#colorRevenue)" strokeWidth={2} name="Revenue" />
                  <Area type="monotone" dataKey="commission" stroke="#8B5CF6" fillOpacity={1} fill="url(#colorCommission)" strokeWidth={2} name="Commission" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export const ConversionReport = () => {
  const conversionData = [
    { month: 'Aug', leads: 45, converted: 28 },
    { month: 'Sep', leads: 52, converted: 35 },
    { month: 'Oct', leads: 61, converted: 42 },
    { month: 'Nov', leads: 78, converted: 51 },
    { month: 'Dec', leads: 95, converted: 65 },
    { month: 'Jan', leads: 112, converted: 78 },
  ];

  return (
    <div className="space-y-6" data-testid="conversion-report">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Conversion Report</h1>
          <p className="text-muted-foreground mt-1">Lead to loan conversion analytics</p>
        </div>
        {/* <Button variant="outline" disabled>
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button> */}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Card className="border border-border"><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Total Leads</p><p className="text-2xl font-bold">443</p></CardContent></Card>
        <Card className="border border-border"><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Converted</p><p className="text-2xl font-bold text-emerald-600">299</p></CardContent></Card>
        <Card className="border border-border"><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Avg Conversion Rate</p><p className="text-2xl font-bold">67.5%</p></CardContent></Card>
        <Card className="border border-border"><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Best Month</p><p className="text-2xl font-bold">Jan (70%)</p></CardContent></Card>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle>Conversion Trend</CardTitle>
          <CardDescription>Leads vs Converted loans</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={conversionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="leads" fill="#3B82F6" name="Total Leads" radius={[4, 4, 0, 0]} />
                <Bar dataKey="converted" fill="#10B981" name="Converted" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const AgentWiseReport = () => {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [serverPaginationEnabled, setServerPaginationEnabled] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    async function load() {
      if (!token) return;
      setLoading(true);
      setError('');
      try {
        const resp = await getAgentReport(token, {
          q: search.trim() || undefined,
          page,
          pageSize: 10,
        });
        if (active) {
          const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
          const meta = resp?.data?.pagination || {};
          setRows(items);
          setServerPaginationEnabled(Boolean(meta.page || meta.page_size || meta.total || meta.total_pages));
          setPagination({
            page: Number(meta.page || page),
            pageSize: Number(meta.page_size || 10),
            total: Number(meta.total || items.length || 0),
            totalPages: Number(meta.total_pages || 1),
          });
        }
      } catch (err) {
        if (!active) return;
        setRows([]);
        setServerPaginationEnabled(false);
        setPagination({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
        setError(err?.message || 'Failed to load agent report');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [page, search, token]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const normalizedSearch = search.trim().toLowerCase();

  const displayRows = useMemo(() => {
    if (serverPaginationEnabled) return rows;
    const filtered = normalizedSearch
      ? rows.filter((agent) =>
          [agent.name, agent.rating]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(normalizedSearch)))
      : rows;
    const start = (page - 1) * pagination.pageSize;
    return filtered.slice(start, start + pagination.pageSize);
  }, [normalizedSearch, page, pagination.pageSize, rows, serverPaginationEnabled]);

  const effectivePagination = useMemo(() => {
    if (serverPaginationEnabled) return pagination;
    const filteredTotal = normalizedSearch
      ? rows.filter((agent) =>
          [agent.name, agent.rating]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(normalizedSearch))).length
      : rows.length;
    const pageSize = pagination.pageSize || 10;
    const totalPages = Math.max(1, Math.ceil(filteredTotal / pageSize));
    return {
      page: Math.min(page, totalPages),
      pageSize,
      total: filteredTotal,
      totalPages,
    };
  }, [normalizedSearch, page, pagination, rows, serverPaginationEnabled]);

  return (
    <div className="space-y-6" data-testid="agent-wise-report">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Agent-wise Report</h1>
          <p className="text-muted-foreground mt-1">Performance breakdown by agent</p>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Search agent..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-[220px]"
          />
          {/* <Button variant="outline" disabled>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button> */}
        </div>
      </div>

      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Total Leads</TableHead>
                <TableHead>Converted</TableHead>
                <TableHead>Conversion %</TableHead>
                <TableHead>Commission Earned</TableHead>
                <TableHead>Pending</TableHead>
                  </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? renderTableMessage(7, 'Loading agent report...') : null}
              {!loading && error ? renderTableMessage(7, error) : null}
              {!loading && !error && displayRows.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell className="font-medium">{agent.name}</TableCell>
                  <TableCell>{agent.totalLeads}</TableCell>
                  <TableCell className="text-emerald-600">{agent.converted}</TableCell>
                  <TableCell>
                    <Badge className={Number(agent.conversion || 0) >= 55 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {formatPercent(agent.conversion)}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{formatCurrency(agent.commissionEarned)}</TableCell>
                  <TableCell className="text-amber-600">{formatCurrency(agent.pendingCommission)}</TableCell>
                
                </TableRow>
              ))}
              {!loading && !error && displayRows.length === 0 ? renderTableMessage(7, 'No agent report data found.') : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Showing page {effectivePagination.page} of {effectivePagination.totalPages} · {effectivePagination.total} agents
        </p>
        <TablePagination page={effectivePagination.page} totalPages={effectivePagination.totalPages} onPageChange={setPage} />
      </div>
    </div>
  );
};

export const BankWiseReport = () => {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [serverPaginationEnabled, setServerPaginationEnabled] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    async function load() {
      if (!token) return;
      setLoading(true);
      setError('');
      try {
        const resp = await getBankReport(token, {
          q: search.trim() || undefined,
          page,
          pageSize: 10,
        });
        if (active) {
          const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
          const meta = resp?.data?.pagination || {};
          setRows(items);
          setPagination({
            page: Number(meta.page || page),
            pageSize: Number(meta.page_size || 10),
            total: Number(meta.total || items.length || 0),
            totalPages: Number(meta.total_pages || 1),
          });
        }
      } catch (err) {
        if (!active) return;
        setRows([]);
        setServerPaginationEnabled(false);
        setPagination({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
        setError(err?.message || 'Failed to load bank report');
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [page, search, token]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const normalizedSearch = search.trim().toLowerCase();

  const fallbackFilteredRows = useMemo(() => {
    if (serverPaginationEnabled) return rows;
    return normalizedSearch
      ? rows.filter((bank) =>
          [bank.name, bank.type, bank.avgInterest]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(normalizedSearch)))
      : rows;
  }, [normalizedSearch, rows, serverPaginationEnabled]);

  const effectivePagination = useMemo(() => {
    if (serverPaginationEnabled) return pagination;
    const pageSize = pagination.pageSize || 10;
    const total = fallbackFilteredRows.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    return {
      page: Math.min(page, totalPages),
      pageSize,
      total,
      totalPages,
    };
  }, [fallbackFilteredRows.length, page, pagination, serverPaginationEnabled]);

  const displayRows = useMemo(() => {
    if (serverPaginationEnabled) return rows;
    const start = (effectivePagination.page - 1) * effectivePagination.pageSize;
    return fallbackFilteredRows.slice(start, start + effectivePagination.pageSize);
  }, [effectivePagination.page, effectivePagination.pageSize, fallbackFilteredRows, rows, serverPaginationEnabled]);

  return (
    <div className="space-y-6" data-testid="bank-wise-report">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Bank-wise Report</h1>
          <p className="text-muted-foreground mt-1">Performance breakdown by bank/NBFC</p>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Search bank..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-[220px]"
          />
          {/* <Button variant="outline" disabled>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button> */}
        </div>
      </div>

      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bank</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Total Loans</TableHead>
                <TableHead>Disbursed Amount</TableHead>
                <TableHead>Revenue Generated</TableHead>
                <TableHead>Avg Interest</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? renderTableMessage(6, 'Loading bank report...') : null}
              {!loading && error ? renderTableMessage(6, error) : null}
              {!loading && !error && displayRows.map((bank) => (
                <TableRow key={bank.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{bank.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={String(bank.type || '').toLowerCase() === 'bank' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                      {bank.type}
                    </Badge>
                  </TableCell>
                  <TableCell>{bank.totalLoans}</TableCell>
                  <TableCell className="font-medium">{formatCurrency(bank.disbursedAmount)}</TableCell>
                  <TableCell className="text-emerald-600 font-medium">{formatCurrency(bank.revenueGenerated)}</TableCell>
                  <TableCell>{bank.avgInterest}</TableCell>
                </TableRow>
              ))}
              {!loading && !error && displayRows.length === 0 ? renderTableMessage(6, 'No bank report data found.') : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Showing page {effectivePagination.page} of {effectivePagination.totalPages} · {effectivePagination.total} banks
        </p>
        <TablePagination page={effectivePagination.page} totalPages={effectivePagination.totalPages} onPageChange={setPage} />
      </div>
    </div>
  );
};

export const ExportData = () => {
  const exports = [
    { name: 'Leads Data', description: 'All leads with status and details', format: 'CSV/Excel' },
    { name: 'Loans Data', description: 'Complete loan applications', format: 'CSV/Excel' },
    { name: 'Agent Performance', description: 'Agent metrics and commissions', format: 'CSV/Excel' },
    { name: 'Commission Report', description: 'All commission transactions', format: 'CSV/Excel' },
    { name: 'Revenue Report', description: 'Monthly revenue breakdown', format: 'PDF' },
    { name: 'Bank Analysis', description: 'Bank-wise performance data', format: 'CSV/Excel' },
  ];

  return (
    <div className="space-y-6" data-testid="export-data">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Export Data</h1>
        <p className="text-muted-foreground mt-1">Download reports and data exports</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {exports.map((item) => (
          <Card key={item.name} className="border border-border">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold">{item.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                  <Badge variant="outline" className="mt-2">{item.format}</Badge>
                </div>
                {/* <Button variant="outline" size="sm" disabled>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button> */}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default RevenueReport;
